import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { Distributor } from '@wsphere/distributors/domain';
import { Staff } from '@wsphere/staff/domain';
import { RoleAssignmentsService, RoleBundlesService, StaffService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-delete-team-member-dialog',
  templateUrl: './delete-team-member.dialog.html',
  standalone: false,
})
export class DeleteTeamMemberDialog {
  constructor(
    private staffSvc: StaffService,
    private toastSvc: ToastService,
    private roleSvc: RoleAssignmentsService,
    private bundleSvc: RoleBundlesService
  ) {}
  @ViewChild(DialogComponent) dialog!: DialogComponent;

  @Input() teamMember?: Staff.Model;
  @Input() distributor?: Distributor.Model;

  @Output() memberRemoved = new EventEmitter();

  _submitting = false;

  async confirm() {
    if (!this.teamMember) throw new Error('Unable to archive team member!');
    this._submitting = true;

    try {
      if (this.distributor) {
        const roles =
          this.teamMember.roles?.filter(role =>
            role.permissionKey?.startsWith(this.distributor?.permissionKey || '')
          ) || [];

        // Direct roles are removed individually; bundle-granted rows must go through their bundle (grouped by
        // bundleId), since the role-remove endpoint only matches direct rows and would 404 on bundle rows.
        const directRoles = roles.filter(role => !role.bundleId);
        const bundleIds = [...new Set(roles.filter(role => role.bundleId).map(role => role.bundleId as string))];
        const staffId = this.teamMember?.id || '';
        const permissionKey = this.distributor?.permissionKey;

        await Promise.all([
          ...directRoles.map(role =>
            this.roleSvc.removeRoleAssignment({ roleId: role.roleId, staffId, permissionKey })
          ),
          ...bundleIds.map(bundleId => this.bundleSvc.removeFromStaff({ bundleId, staffId, permissionKey })),
        ]);

        this.toastSvc.show({
          type: 'success',
          title: 'Member removed',
          text: `Removed ${this.teamMember?.name ?? 'user'} from ${this.distributor.name}`,
        });
      } else {
        await this.staffSvc.deactivate(this.teamMember.id);

        this.toastSvc.show({
          type: 'success',
          title: 'User removed from team',
          text: `${this.teamMember?.name || 'This user'} has been removed from your team.`,
        });
      }
    } catch (e) {
      console.error(e);
      this.toastSvc.show({
        type: 'error',
        title: 'Unexpected error',
        text: 'Something went wrong and the member was not removed. Please try again.',
      });
    }

    this._submitting = false;
    this.closeDialog();
  }

  openDialog() {
    this.dialog.open();
  }

  closeDialog() {
    this.memberRemoved.emit();
    this.dialog.close();
  }
}
